import { Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { SMSMutationContext, useSMSDataV3QueryUpdaters } from '../query-updaters';
import { SchemaSMSDataV3Service } from '../service';
import { OptimisticUpdateStatusDetails, ResendSMSIO } from '../types';

export const getResendSMSMutationKey = () => ['V3', 'ResendSMS'] as const;

type MutationContext<C = unknown> = {
  internalContext?: SMSMutationContext;
  otherContext?: C;
};

type ExtendedResendSMSRequest = ResendSMSIO['input'] & {
  threadId?: string;
  locationId?: string;
};

type UseResendSMSMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
> = {
  options?: LimitedSchemaMutationOptions<
    { input: ExtendedResendSMSRequest; output: ResendSMSIO['output'] },
    E,
    C,
    never,
    RequestOverride
  >;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `ResendSMS` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useResendSMSMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
>({
  options,
  httpOptions,
  optimisticUpdate = false,
}: UseResendSMSMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const { updateSMS } = useSMSDataV3QueryUpdaters();

  return useMutation<ResendSMSIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getResendSMSMutationKey(),
    mutationFn: (req) => SchemaSMSDataV3Service.ResendSMS(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      let internalContext: SMSMutationContext | undefined = undefined;
      if (optimisticUpdate) {
        internalContext = updateSMS({
          matchValues: {
            id: request.smsId,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          newValues: {
            status: Status.STATUS_NOT_SENT,
            statusDetails: OptimisticUpdateStatusDetails.SENDING,
          },
        });
      }

      return {
        internalContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: (response, request, context) => {
      updateSMS({
        matchValues: {
          id: response.sms.id,
          threadId: response.sms.threadId,
          locationId: response.sms.locationId,
        },
        newValues: response.sms,
      });

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (optimisticUpdate && context?.internalContext?.originalSMS) {
        updateSMS({
          matchValues: {
            id: request.smsId,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          newValues: context.internalContext.originalSMS,
        });
      }
      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      // Only pass context of type C into provided `onSettled` option
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
